import React, { useMemo } from "react";

import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import get from "lodash/get";
import omit from "lodash/omit";
import pick from "lodash/pick";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Button from "../shared/Button";
import SelectController from "../forms/SelectController";
import SponsorReportForm from "./SponsorReportForm";
import { useFormValuesContext } from "../context/FormValuesContext";
import { getDocTypesWithTemplate } from "../../helpers/utils";

const NewDocumentForm = ({
  estate,
  contacts,
  isProject,
  openSelectUnitsModal,
}) => {
  const navigate = useNavigate();
  const { values, setValues } = useFormValuesContext();
  const { id: estateId } = estate;

  const { control, handleSubmit, getValues, watch, setValue } = useForm({
    defaultValues: {
      estate,
      documentType: "",
      ...omit(values, ["officeData"]),
    },
  });

  const onSubmit = (data) => {
    const selectedOffice = get(data, "office");
    if (!!selectedOffice && selectedOffice !== "none") {
      setValues(data);
      !!openSelectUnitsModal && openSelectUnitsModal();
    } else {
      const baseUrl = `/new-document/${estateId}`;
      const startDate = dayjs(data.startDate).locale("fr").format("L");
      const endDate = dayjs(data.endDate).locale("fr").format("L");
      const searchParams = new URLSearchParams({
        ...pick(data, ["documentType", "owner"]),
        startDate,
        endDate,
      });
      const url = `${baseUrl}?${searchParams.toString()}`;
      navigate(url);
    }
  };

  const documentType = useMemo(
    () => getValues("documentType"),
    [watch("documentType")]
  );

  return (
    <StyleWrapper className="d-flex flex-column rounded-box">
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 26,
          marginBottom: 2,
        }}
      >
        Créer un nouveau document
      </Typography>
      <Stack
        component="form"
        gap={2}
        onSubmit={handleSubmit(onSubmit, (error) => console.log(error))}
      >
        <Stack gap={1}>
          <label className="form-label">Choisissez le type de document</label>
          <SelectController
            control={control}
            rules={{ required: true }}
            name="documentType"
            options={isProject ? getDocTypesWithTemplate() : []}
            sx={{ backgroundColor: "var(--white)" }}
          />
        </Stack>
        {(documentType === 1 || documentType === 2 || documentType === 3) && (
          <SponsorReportForm
            control={control}
            contacts={contacts}
            getValues={getValues}
            watch={watch}
            setValue={setValue}
          />
        )}
        <Stack flexDirection="row" justifyContent="end" gap={2}>
          <Button type="button" className="cancel-btn">
            Annuler
          </Button>
          <Button
            type="submit"
            className="create-btn"
            variant="contained"
            color="primary"
          >
            Créer
          </Button>
        </Stack>
      </Stack>
    </StyleWrapper>
  );
};

const StyleWrapper = styled.div`
  &.rounded-box {
    padding: 30px;
    background-color: var(--light-grey-bg);
  }

  .form-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--label-color);

    &:not(.optional)::after {
      content: " *";
      color: var(--red-color);
    }

    &.optional::after {
      content: " (optionnel)";
    }
  }

  .cancel-btn {
    width: 168px;
  }

  .create-btn {
    width: 222px;
  }
`;

export default NewDocumentForm;
